import PageSearch from '../../molecules/PageSearch';
import { useNavigate, useParams } from 'react-router-dom';
import PageList from '../../templates/PageList';
import { ATableHeader } from '../../organisms/ATable';
import Tooltip from '../../atoms/Tooltip';
import { FaMailBulk, FaHtml5 } from 'react-icons/fa';
import { FaWhatsapp } from 'react-icons/fa6';
import appConfig from '../../../config/app-config';

interface TemplatePage {
    id: string;
    name: string;
    description: string;
    createdAt: string;
    templatesCount: number;
    variablesCount: number;
    usersCount: number;
    active: boolean;
}

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const API_KEY = COMMUNICATION_ENV.key;
const BASE_URL = COMMUNICATION_ENV.address;

const TemplatesPageList = () => {
    const navigate = useNavigate();
    const { page, pageSize, field: fieldSorted, order: orderSorted, filter }: any = useParams();

    let txtName = '';
    if (filter) {
        [txtName = ''] = filter.match(/=(.*?);/g).map((m: string) => m.slice(1, -1));
    }

    const url = `${BASE_URL}/templates/page-list?name=${txtName}&page=${page}&pageSize=${pageSize}&order=${fieldSorted},${orderSorted}`;

    const tableHeaders: ATableHeader[] = [
        { id: 'col-name', title: 'Nome', field: 'name', canSort: true, width: '330px' },
        { id: 'col-description', title: 'Descrição', field: 'description', canSort: true },
        { id: 'col-createdAt', title: 'Criado em', field: 'createdAt', canSort: true, width: '140px' },
        { id: 'col-templates', title: 'Modelos', field: 'templatesCount', canSort: false, width: '80px' },
        { id: 'col-variables', title: 'Variáveis', field: 'variablesCount', canSort: false, width: '80px' },
        { id: 'col-users', title: 'Usuários', field: 'usersCount', canSort: false, width: '80px' },
        { id: 'col-active', title: 'Status', field: 'active', canSort: false, width: '80px' },
        { id: 'col-html', title: '', field: '', canSort: false, width: '40px' },
    ];

    const getCssColorBy = (active: boolean) => (active ? 'bg-green-500' : 'bg-red-500');

    const renderRows = (rows: TemplatePage[]) => {
        if (!rows || rows.length === 0) {
            return (
                <tr>
                    <td colSpan={8} className="py-2 px-2 text-center">
                        <div className="text-2xl font-bold text-gray-500 mt-4">Nenhum modelo encontrado...</div>
                    </td>
                </tr>
            );
        }

        return rows.map((template: TemplatePage, index: number) => (
            <tr
                key={template.id}
                onClick={() => navigate(`/communication/template/${template.name}`)}
                className={`${index % 2 === 0 ? 'bg-slate-100 dark:bg-slate-800' : 'dark:bg-slate-900'} hover:bg-slate-300 hover:shadow-md border-b border-gray-300 cursor-pointer`}
            >
                <td className="py-2 px-2">
                    <div className="flex items-center space-x-2">
                        {template.id === "WPP_TEMPLATE" ? (
                            <FaWhatsapp className="text-green-500" title="WhatsApp Template" />
                        ) : (
                            <FaMailBulk className="text-blue-500" title="Email Template" />
                        )}
                        <span>{template.name}</span>
                    </div>
                </td>
                <td className="py-2 px-2 max-w-0 truncate">
                    <Tooltip content={template.description} alignment="bottom">
                        <span className="block truncate">{template.description}</span>
                    </Tooltip>
                </td>
                <td className="py-2 px-2">{template.createdAt}</td>
                <td className="py-2 px-2" align="center">{template.templatesCount}</td>
                <td className="py-2 px-2" align="center">{template.variablesCount}</td>
                <td className="py-2 px-2" align="center">{template.usersCount}</td>
                <td className="py-2 px-2" align="center">
                    <span className={`rounded px-1 py-1 text-xs font-bold text-white ${getCssColorBy(template.active)}`}>{template.active ? 'Ativo' : 'Inativo'}</span>
                </td>
                <td className="py-2 px-2" align="center" onClick={(e) => e.stopPropagation()}>
                    {template.id === 'MAIL_TEMPLATE' && (
                        <button type="button" className="text-blue-500 hover:text-blue-700">
                            {/* TODO: open modal with HTML content */}
                            <FaHtml5 />
                        </button>
                    )}
                </td>
            </tr>
        ));
    };

    const pageChanged = (page: number) => {
        navigate(urlNavigate(page - 1, pageSize, fieldSorted, orderSorted, txtName), { replace: true });
    };

    const pageSizeChanged = (size: number) => {
        navigate(urlNavigate(0, size, fieldSorted, orderSorted, txtName), { replace: true });
    };

    const pageSorted = (field: keyof TemplatePage | string, order: 'ASC' | 'DESC') => {
        navigate(urlNavigate(page, pageSize, field, order, txtName), { replace: true });
    };

    const txtNameChanged = (name: string) => {
        navigate(urlNavigate(0, pageSize, fieldSorted, orderSorted, name), { replace: true });
    };

    const onFilterClean = () => {
        navigate(urlNavigate(0, pageSize, fieldSorted, orderSorted, ''), { replace: true });
    };

    const urlNavigate = (
        page: number,
        pageSize: number,
        fieldSorted: string,
        orderSorted: string,
        name: string,
    ) => {
        return `/communication/templates/${page}/${pageSize}/${fieldSorted}/${orderSorted}/n=${name};`;
    };

    return (
        <PageList
            pageTitle="Modelos"
            url={url}
            fetchHeaders={[{ key: 'x-api-key', value: API_KEY }]}
            breadcrumb={[{ label: 'Modelos', url: '#' }]}
            tableFieldSort={fieldSorted}
            tableOrderSort={orderSorted}
            onTableSort={pageSorted}
            renderRows={renderRows}
            page={page}
            pageSize={pageSize}
            onPageChange={pageChanged}
            onItemsPerPageChange={pageSizeChanged}
            itemsLabel="modelos"
            defaultBackendHeader={false}
            tableHeaders={tableHeaders}
        >
            {{
                filterSlot: (
                    <div className="grid grid-cols-12 gap-1">
                        <PageSearch
                            value={txtName}
                            label="Procurar por nome..."
                            onSearch={txtNameChanged}
                            filterCleanVisible={true}
                            filterCleanEnabled={Boolean(txtName)}
                            onFilterClean={onFilterClean}
                            configureVisible={false}
                            className="col-span-12"
                        />
                    </div>
                ),
                controlSlot: <></>,
            }}
        </PageList>
    );
};

export default TemplatesPageList;
